import type { CSSProperties, ReactElement } from 'react';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import { ColorType } from '@/theme/colors';
import { FontWeight } from '@/theme/typography';
import { Cmarkdowntext } from '../Cmarkdowntext';
import { Cscrollable } from '../Cscrollable';
import { POPOVER_ROW_BORDER_BOTTOM, POPOVER_ROW_BORDER_BOTTOM_NONE, POPOVER_TEXT_TRANSFORM_UPPERCASE, POPOVER_WORD_BREAK, TABLE_SCROLL_DIRECTION, TABLE_STYLE_VALUE, } from './Cmarkdowntable.constants';
import type { CmarkdowntablePopoverEntry } from './Cmarkdowntable.types';
// These two rows were authored as raw px on plain divs, off the `pxToRem` scale
// the `size` prop feeds (`BASE_FONT_SIZE` 15 against a 16px root, so `size={10}`
// would render 10.67px) — the font size therefore stays here as `fontSize` and
// no `size` prop is passed. Line-height is likewise carried verbatim: the title
// inherited `normal` and the value pinned 1.5, so neither takes the markdown
// paragraph's body-copy 1.65.
const MARKDOWN_TABLE_POPOVER_ROW_TITLE_TEXT_STYLE: CSSProperties = {
    fontSize: 10,
    lineHeight: 'normal',
    letterSpacing: '0.14em',
    textTransform: POPOVER_TEXT_TRANSFORM_UPPERCASE,
};
const MARKDOWN_TABLE_POPOVER_ROW_VALUE_TEXT_STYLE: CSSProperties = {
    fontSize: 13,
    lineHeight: 1.5,
    wordBreak: POPOVER_WORD_BREAK,
    overflowWrap: TABLE_STYLE_VALUE.OverflowWrapBreakWord,
};
interface CmarkdowntablePopoverEntriesProps {
    dataElScope: DataElScope;
    entries: CmarkdowntablePopoverEntry[];
    panelHeight?: number;
    vh: number;
}
export function CmarkdowntablePopoverEntries({ dataElScope, entries, panelHeight, vh, }: CmarkdowntablePopoverEntriesProps): ReactElement {
    return (<Cscrollable dataElScope={dataElScope} direction={TABLE_SCROLL_DIRECTION.Vertical} fadeSize={16} firstItemSpacing={0} style={{
            flex: 1,
            minHeight: 0,
            maxHeight: panelHeight == null ? vh * 46 : undefined,
            width: TABLE_STYLE_VALUE.WidthFull,
        }}>
        <div style={{
            padding: '4px 16px 14px',
            display: 'flex',
            flexDirection: 'column',
        }}>
          {entries.map((entry, columnIndex) => {
            const isIdentifier = columnIndex === 0;
            const entryIdentity = entry.title || `row-${columnIndex}`;
            return (<div data-el={dataEl(dataElScope, 'PopoverEntries/Entry')} key={`popover-kvrow-${columnIndex}`} style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 5,
                    borderBottom: columnIndex < entries.length - 1
                        ? POPOVER_ROW_BORDER_BOTTOM
                        : POPOVER_ROW_BORDER_BOTTOM_NONE,
                    paddingTop: 10,
                    paddingBottom: 10,
                }}>
                <Cmarkdowntext dataElScope={dataElScope} content={entry.title} weight={FontWeight.SemiBold} color={ColorType.White} opacity={0.4} textStyle={MARKDOWN_TABLE_POPOVER_ROW_TITLE_TEXT_STYLE}/>
                <Cmarkdowntext dataElScope={dataElScope} content={entry.value} weight={isIdentifier ? FontWeight.SemiBold : FontWeight.Medium} color={isIdentifier ? ColorType.Accent : ColorType.White} opacity={isIdentifier ? 1 : 0.92} textStyle={MARKDOWN_TABLE_POPOVER_ROW_VALUE_TEXT_STYLE}/>
              </div>);
        })}
        </div>
    </Cscrollable>);
}
